import { BrandResponse } from "../../../types/BrandModels";
import { BrandFormData } from "../hooks/useBrandForm";
import { canonicalHexColor } from "./brandFormValidation";

/**
 * Maps backend BrandResponse to frontend BrandFormData
 * Handles nested structure and field name conversions
 */
export const mapBrandResponseToFormData = (
  brandResponse: BrandResponse
): BrandFormData => {
  return {
    brandName: brandResponse.brand?.name || "",
    businessType: brandResponse.brand?.business_type || "",
    industry: brandResponse.brand?.industry || "",
    website: brandResponse.brand?.website_url || "",
    phoneNumber: brandResponse.brand?.phone_number || "",
    serviceArea: brandResponse.brand?.service_areas || [],
    servicesOffered: brandResponse.brand?.services || [],
    productsOffered: brandResponse.brand?.products || [],
    tagline: brandResponse.brand?.tagline || "",
    logoUrl: brandResponse.visual?.logo_url || "",
    logoMediaId: brandResponse.visual?.logo_media_id || "",
    // Absent on every brand saved before SGL-344, which is the common case and not
    // an error — an empty dark slot means "no dark-background variant set".
    logoUrlDark: brandResponse.visual?.logo_url_dark || "",
    logoMediaIdDark: brandResponse.visual?.logo_media_id_dark || "",
    // Falls back to an empty palette, never to a colour (SGL-224). A brand record
    // with no colours means the customer has not set any — saying so honestly is
    // correct. Substituting Sagelyn's #2f4941 here wrote our accent green into
    // customer profiles that had never chosen a palette at all.
    colors:
      brandResponse.visual?.colors?.map((c) => ({
        value: c.hex,
        type: c.role as "primary" | "secondary" | "accent" | "other",
      })) || [],
    brandVoice: brandResponse.content_guidelines?.brand_voice || "",
    targetAudience: brandResponse.content_guidelines?.target_audience || "",
    primaryCTA: brandResponse.content_guidelines?.primary_cta || [],
    avoidWords: brandResponse.content_guidelines?.avoid_phrases || [],
    mustIncludeWords: brandResponse.content_guidelines?.must_include_phrases || [],
  };
};

/**
 * Maps frontend BrandFormData to backend payload format
 * Used when preparing data for POST/PUT requests
 */
export const mapFormDataToBrandPayload = (formData: BrandFormData) => {
  return {
    brand_name: {
      answer_value: formData.brandName,
    },
    business_type: {
      answer_value: formData.businessType,
    },
    industry: {
      answer_value: formData.industry,
    },
    website: {
      answer_value: formData.website,
    },
    phone_number: {
      answer_value: formData.phoneNumber,
    },
    service_area: {
      answer_value: formData.serviceArea,
    },
    services_offered: {
      answer_value: formData.servicesOffered,
    },
    products_offered: {
      answer_value: formData.productsOffered,
    },
    brand_tagline: {
      answer_value: formData.tagline,
    },
    brand_voice: {
      answer_value: formData.brandVoice,
    },
    target_audience: {
      answer_value: formData.targetAudience,
    },
    primary_cta: {
      answer_value: formData.primaryCTA,
    },
    avoid_words: {
      answer_value: formData.avoidWords,
    },
    must_include_words: {
      answer_value: formData.mustIncludeWords,
    },
    logo_url: formData.logoUrl,
    logo_media_id: formData.logoMediaId,
    logo_url_dark: formData.logoUrlDark,
    logo_media_id_dark: formData.logoMediaIdDark,
    // Canonical on the way out, so a brand saved before SGL-342 — when the
    // picker stored whatever was typed — is repaired the next time it is saved,
    // rather than carrying `648B7F` into every render that reads the palette.
    brand_colors: formData.colors.map(
      (c) => canonicalHexColor(c.value) ?? c.value
    ),
  };
};
